/**
 * EntityMetrics dashboard page — admin-only route that provides operational
 * visibility into campaign and workflow health via two tabs.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import { useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useTranslation } from 'src/arb-wrapper';
import { OverviewTab } from 'src/components/entity-metrics/OverviewTab';
import { HealthTab } from 'src/components/entity-metrics/HealthTab';
import { type TimePeriodValue } from 'src/components/entity-metrics/timePeriodUtils';

type ActiveTab = 'overview' | 'health';

const VALID_TABS: ReadonlySet<string> = new Set(['overview', 'health']);
const VALID_TIME_PERIODS: ReadonlySet<string> = new Set(['1d', '7d', '30d', '3m', '6m', '12m']);

/** Parse and validate a tab param from URL */
const parseTabParam = (value: string | null): ActiveTab =>
  value && VALID_TABS.has(value) ? (value as ActiveTab) : 'overview';

/** Parse and validate a time period param from URL */
const parseTimePeriodParam = (value: string | null, fallback: TimePeriodValue): TimePeriodValue =>
  value && VALID_TIME_PERIODS.has(value) ? (value as TimePeriodValue) : fallback;

/** Parse pie period which also allows 'all' */
const parsePiePeriodParam = (value: string | null): TimePeriodValue | 'all' =>
  value === 'all' || !value ? 'all' : (VALID_TIME_PERIODS.has(value) ? (value as TimePeriodValue) : 'all');

export const EntityMetrics: React.FC = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('entity_metrics'));

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = parseTabParam(searchParams.get('tab'));
  const healthPeriod = parseTimePeriodParam(searchParams.get('healthPeriod'), '7d');
  const chartPeriod = parseTimePeriodParam(searchParams.get('chartPeriod'), '6m');
  const piePeriod = parsePiePeriodParam(searchParams.get('piePeriod'));

  const setActiveTab = (tab: ActiveTab) => {
    setSearchParams((prev) => {
      if (tab === 'overview') {
        prev.delete('tab');
      } else {
        prev.set('tab', tab);
      }
      return prev;
    }, { replace: true });
  };

  const setHealthPeriod = (value: TimePeriodValue) => {
    setSearchParams((prev) => {
      if (value === '7d') {
        prev.delete('healthPeriod');
      } else {
        prev.set('healthPeriod', value);
      }
      return prev;
    }, { replace: true });
  };

  const setChartPeriod = (value: TimePeriodValue) => {
    setSearchParams((prev) => {
      if (value === '6m') {
        prev.delete('chartPeriod');
      } else {
        prev.set('chartPeriod', value);
      }
      return prev;
    }, { replace: true });
  };

  const setPiePeriod = (value: TimePeriodValue | 'all') => {
    setSearchParams((prev) => {
      if (value === 'all') {
        prev.delete('piePeriod');
      } else {
        prev.set('piePeriod', value);
      }
      return prev;
    }, { replace: true });
  };

  return (
    <Column spacing="400">
      <Heading level={1}>{t('entity_metrics')}</Heading>

      <TabGroup value={activeTab} onChange={setActiveTab}>
        <Tab value="overview">{t('tab_overview')}</Tab>
        <Tab value="health">{t('tab_sync_health')}</Tab>
      </TabGroup>

      {activeTab === 'overview' && (
        <OverviewTab
          chartPeriod={chartPeriod}
          piePeriod={piePeriod}
          onChartPeriodChange={setChartPeriod}
          onPiePeriodChange={setPiePeriod}
        />
      )}
      {activeTab === 'health' && (
        <HealthTab period={healthPeriod} onPeriodChange={setHealthPeriod} />
      )}
    </Column>
  );
};
